import React, { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { IResponse } from "constants/interfaces"

import useApi from "util/api"

interface User {
  id: string
  username: string
  email_address: string
  created_at: string
  personal_profile?: {
    country?: string
  }
}

interface UserPickerProps {
  selectedUserId: string
  onSelect: (userId: string, username: string) => void
  disabled?: boolean
}

export const UserPicker: React.FC<UserPickerProps> = ({ selectedUserId, onSelect, disabled }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [searchQuery, setSearchQuery] = useState("")
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedUsername, setSelectedUsername] = useState("")
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchUsers()
      } else {
        setUsers([])
        setShowDropdown(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  const searchUsers = async () => {
    setLoading(true)
    try {
      const { data } = await useApi.get<IResponse<{ users?: User[]; data?: User[]; total: number }>>("/admin/dashboard/users", {
        params: {
          q: searchQuery,
          page: 1,
          pageSize: 10,
          sortRow: "created_at",
          sortOrder: "desc"
        }
      })

      if (data?.success && data.response) {
        const usersList = data.response.users || data.response.data || []
        setUsers(usersList)
        if (usersList.length > 0) {
          setShowDropdown(true)
        }
      }
    } catch (error) {
      console.error("Failed to search users:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectUser = (user: User) => {
    setSelectedUsername(user.username)
    setSearchQuery("")
    setUsers([])
    setShowDropdown(false)
    onSelect(user.id, user.username)
  }

  const handleClear = () => {
    setSearchQuery("")
    setSelectedUsername("")
    setUsers([])
    setShowDropdown(false)
    onSelect("", "")
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {selectedUserId && selectedUsername ? (
        <div className="bg-[#0C0C1A] border border-emerald-600 rounded px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-sm text-gray-400 mb-1">{t("bonus2.shared.selectedUser")}</div>
              <div className="text-white font-semibold">{selectedUsername}</div>
              <div className="text-xs text-gray-500 font-mono mt-1">ID: {selectedUserId}</div>
            </div>
            <button
              onClick={handleClear}
              className="ml-3 px-3 py-1 text-sm bg-rose-400 text-white rounded hover:bg-rose-700 transition-colors"
              type="button"
            >
              {t("bonus2.shared.changeUser")}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery.length >= 2 && users.length > 0 && setShowDropdown(true)}
              placeholder={t("bonus2.shared.searchByUsernameOr")}
              className="w-full bg-[#0C0C1A] text-white border border-gray-600 rounded px-4 py-2 pr-20 focus:outline-none focus:border-purple-500"
              // Do NOT disable while the async search is in flight: toggling `disabled` on
              // a focused input makes the browser BLUR it, so the field lost focus mid-typing
              // every time results came back (the spinner already shows the loading state).
              // Only honour the parent's explicit `disabled`.
              disabled={disabled}
            />

            {loading && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-purple-500 rounded-full border-t-transparent"></div>
              </div>
            )}

            {searchQuery && (
              <button
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-gray-400 hover:text-white transition-colors"
                type="button"
              >
                ✕
              </button>
            )}
          </div>

          {!searchQuery && <p className="mt-1 text-xs text-gray-500">{t("bonus2.shared.typeAtLeast2")}</p>}

          {showDropdown && users.length > 0 && !loading && (
            <div className="absolute z-50 w-full mt-1 bg-[#1A1A2E] border border-gray-600 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className="w-full px-4 py-3 text-left hover:bg-[#2A2A3E] transition-colors border-b border-gray-700 last:border-b-0"
                  type="button"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-white font-semibold">{user.username}</div>
                      <div className="text-sm text-gray-400">{user.email_address}</div>
                      <div className="text-xs text-gray-500 mt-1 font-mono">ID: {user.id}</div>
                    </div>
                    {user.personal_profile?.country && <div className="text-xs text-gray-500 ml-2">{user.personal_profile.country}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {showDropdown && !loading && users.length === 0 && searchQuery.length >= 2 && (
            <div className="absolute z-50 w-full mt-1 bg-[#1A1A2E] border border-gray-600 rounded-lg shadow-lg p-4 text-center text-gray-400">
              {t("bonus2.shared.noUsersFound")}
            </div>
          )}
        </>
      )}
    </div>
  )
}
